/*
 * Close versions tab: view state, its URL form and the state transitions. Pure; the wire builders
 * are in requests.js.
 */
import { build_query_string } from '../overview/params';
import { month_from_search } from '../shared/month_query';
import { merge_search, uuid_param } from '../shared/url_query';

export const MODE_REOPENED_NOW = 'REOPENED_NOW';
export const MODE_CLOSE_VERSIONS = 'CLOSE_VERSIONS';
export const MODES = [MODE_REOPENED_NOW, MODE_CLOSE_VERSIONS];

export const SORT_KEYS = [
  'abs_net_diff',
  'net_diff',
  'teacher_name',
  'baseline_net',
  'current_net',
  'version_count',
  'changed_at',
  'status',
  'reopen_reason'
];
export const DEFAULT_SORT = 'abs_net_diff';
export const DEFAULT_DIR = 'desc';
const DIRS = ['asc', 'desc'];

export const PAGE_SIZE = 100;
export const QUERY_MAX_LEN = 100;
export const QUERY_TOO_LONG_TEXT = `Search is limited to ${QUERY_MAX_LEN} characters.`;

// The server validates the uuid itself; this only keeps a typed URL from sending arbitrary text.

export const TAB_PATH = '/teacher-payments/close-versions';

const in_list = (list, v) => list.indexOf(v) !== -1;
const bool_param = (v) => (v === 'true' ? true : v === 'false' ? false : null);

// multi_version null = the mode's default (on in Close versions); Reopened now never sends true.
export const blank_state = () => ({
  mode: '',
  only_changed: false,
  multi_version: null,
  q: '',
  sort: DEFAULT_SORT,
  dir: DEFAULT_DIR,
  offset: 0,
  teacher_uuid: ''
});

// The URL's short names; the old long names (mode=, only_changed=, multi_version=, dir=,
// teacher_uuid=) are still read so earlier links keep working, and never written.
const MODE_KEYS = { reopened: MODE_REOPENED_NOW, versions: MODE_CLOSE_VERSIONS };
const mode_key = (mode) => Object.keys(MODE_KEYS).find((k) => MODE_KEYS[k] === mode) || '';
const flag_param = (v) => (v === '1' ? true : v === '0' ? false : null);

// mode, offset and teacher belong to the URL's month; a URL from another month keeps only filters and sort.
export const parse_state = (search, pay_month) => {
  const qs = new URLSearchParams(typeof search === 'string' ? search : '');
  const get = (k) => (qs.get(k) || '').trim();
  const link_month = month_from_search(search);
  const same_month = !link_month || link_month === pay_month;
  const offset = Number(get('offset'));
  const q = get('q');
  const mode = MODE_KEYS[get('view')] || (in_list(MODES, get('mode')) ? get('mode') : '');
  const changed = flag_param(get('changed'));
  const multi = flag_param(get('multi'));
  const [sort_key, sort_dir] = get('sort').split('|');
  const dir = sort_dir || get('dir');
  const teacher = get('teacher') || get('teacher_uuid');
  const s = blank_state();
  return {
    ...s,
    mode: same_month ? mode : '',
    only_changed: changed === null ? bool_param(get('only_changed')) === true : changed,
    multi_version: multi === null ? bool_param(get('multi_version')) : multi,
    // q is never written (a name or email is not put in the URL); an old link's q is still read.
    q: q.length <= QUERY_MAX_LEN ? q : '',
    sort: in_list(SORT_KEYS, sort_key) ? sort_key : s.sort,
    dir: in_list(SORT_KEYS, sort_key) && in_list(DIRS, dir) ? dir : s.dir,
    offset:
      same_month && Number.isInteger(offset) && offset > 0 && offset % PAGE_SIZE === 0 ? offset : 0,
    teacher_uuid: same_month ? uuid_param(teacher) : ''
  };
};

// an explicit mode (URL or a click) wins; else the server's default_mode.
export const effective_mode = (state, summary) => {
  if (in_list(MODES, state.mode)) return state.mode;
  const d = summary && summary.default_mode;
  return in_list(MODES, d) ? d : '';
};

export const multi_version_on = (state, mode) =>
  mode === MODE_CLOSE_VERSIONS && state.multi_version !== false;

// Switching resets the page and the mode-specific filter, and keeps the sort.
export const with_mode = (state, mode) => ({ ...state, mode, multi_version: null, offset: 0 });

export const with_filter = (state, patch) => ({ ...state, ...patch, offset: 0 });

// The same key flips the direction; a new key starts on its natural order (names A-Z, amounts biggest first).
export const with_sort = (state, key) => {
  if (!in_list(SORT_KEYS, key)) return state;
  if (state.sort === key) {
    return { ...state, dir: state.dir === 'asc' ? 'desc' : 'asc', offset: 0 };
  }
  return { ...state, sort: key, dir: key === 'teacher_name' ? 'asc' : 'desc', offset: 0 };
};

export const has_filters = (state, mode) =>
  state.only_changed ||
  state.q !== '' ||
  (mode === MODE_CLOSE_VERSIONS && state.multi_version === false);

// "Clear filters" returns to the mode's defaults, multi_version included.
export const clear_filters = (state) => ({
  ...state,
  only_changed: false,
  multi_version: null,
  q: '',
  offset: 0
});

const STATE_SEARCH_KEYS = [
  'view',
  'changed',
  'multi',
  'sort',
  'offset',
  'teacher',
  // legacy names: removed on write
  'mode',
  'only_changed',
  'multi_version',
  'q',
  'dir',
  'teacher_uuid'
];

// The URL the tab writes back: only what differs from the defaults, merged into the current search
// so the shell's ?month= (or any other param) survives.
export const state_search = (search, state) => {
  const s = blank_state();
  const sorted = state.sort !== s.sort || state.dir !== s.dir;
  const own = {
    view: mode_key(state.mode),
    changed: state.only_changed ? '1' : '',
    multi: state.multi_version === null ? '' : state.multi_version ? '1' : '0',
    sort: sorted ? `${state.sort}|${state.dir}` : '',
    offset: state.offset > 0 ? state.offset : '',
    teacher: state.teacher_uuid
  };
  const qs = new URLSearchParams(merge_search(search, STATE_SEARCH_KEYS, own));
  // The month now rides on the shell's ?month=; a legacy pay_month= beside it would go stale.
  if (qs.get('month')) qs.delete('pay_month');
  return qs.toString();
};

// Overview's "Versions" link: the tab opens with the teacher-month's versions dialog over the list.
// Still the legacy names (pinned by overview's own test); parse_state reads both forms.
export const versions_link = (pay_month, teacher_uuid) =>
  `${TAB_PATH}?${build_query_string({ pay_month, teacher_uuid })}`;

// every teacher-month closed now or closed before (reopen_count > 0).
export const shows_versions_link = (row) =>
  !!row && (row.lifecycle === 'CLOSED' || Number(row.reopen_count) > 0);

// The sentence under the filters, the Overview's shape; defaults spelled out. Null while loading.
export const cv_filter_summary = ({ state, mode, total, month_label, loading }) => {
  if (loading || !mode) return null;
  const noun = total === 1 ? 'teacher-month' : 'teacher-months';
  const on = has_filters(state, mode);
  const parts = [];
  if (state.q) parts.push(`name or email matches “${state.q}”`);
  parts.push(state.only_changed ? 'changed only' : 'changed or not');
  if (mode === MODE_CLOSE_VERSIONS) {
    parts.push(multi_version_on(state, mode) ? 'closed more than once' : 'first closes too');
  }
  return on
    ? { lead: `${total} ${noun}`, rest: ` for ${month_label}: ${parts.join(', ')}.` }
    : { lead: `all ${total} ${noun}`, rest: ` for ${month_label}, ${parts.join(', ')}.` };
};
